import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getGoogleBusinessReviewsStatisticsStore, getGoogleBussinessesStore } from '../../../../store/ratings/thunks';
import { 
  Box, Grid, Card, CardContent, Typography, Avatar, Rating, CircularProgress, Alert, Paper, Autocomplete, TextField, Chip, alpha, useTheme 
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import StarIcon from '@mui/icons-material/Star';
import GoogleIcon from '@mui/icons-material/Google';
import ReplyIcon from '@mui/icons-material/Reply';
import AutocompleteField from 'components/formFields/autocompleteField';

import { Formik, Form } from 'formik';

// Tipos basados en la estructura de datos
interface RatingDistribution {
  "1": number;
  "2": number;
  "3": number;
  "4": number;
  "5": number;
}

interface Statistics {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: RatingDistribution;
}

interface Reviewer {
  profilePhotoUrl?: string;
  displayName: string;
}

interface ReviewReply {
  comment: string;
  updateTime: string;
}

interface Review {
  reviewId: string;
  reviewer: Reviewer;
  starRating: string;
  comment: string;
  createTime: string;
  updateTime: string;
  reviewReply?: ReviewReply;
}

interface GoogleReviewsData {
  statistics?: Statistics;
  reviews?: Review[];
}

const mapStarRatingToNumber = (ratingStr: string | number) => {
  if (!ratingStr) return 0;
  const parsed = parseFloat(ratingStr as string);
  if (!isNaN(parsed)) return parsed;
  const map: Record<string, number> = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
  return map[ratingStr as string] || 0;
};

export const Estadisticas = () => {
  const dispatch = useDispatch<any>();
  const theme = useTheme();
  const [data, setData] = useState<GoogleReviewsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);

  const { status }: any = useSelector((state: any) => state.loading);
  const { google_bussinesses_list_for_select } = useSelector((state: any) => state.ratingsDevices);

  useEffect(() => {
    dispatch(getGoogleBussinessesStore());
  }, [dispatch]);

  useEffect(() => {
    if (google_bussinesses_list_for_select && google_bussinesses_list_for_select.length > 0 && !selectedLocation) {
        setSelectedLocation(google_bussinesses_list_for_select[0]);
    }
  }, [google_bussinesses_list_for_select]);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedLocation) {
        try {
          const res = await dispatch(getGoogleBusinessReviewsStatisticsStore(selectedLocation.value));
          if (res && res.ok && res.data) {
            setData(res.data);
            setError(null);
          } else {
            setError("No se pudieron cargar las estadísticas. Verifica la conexión con Google Business.");
            setData(null);
          }
        } catch (err: any) {
          setError("Ocurrió un error al cargar los datos.");
          setData(null);
        }
      }
    };

    fetchData();
  }, [dispatch, selectedLocation]);

  

  if (status === 'checking') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} color="primary" />
      </Box>
    );
  }

  if (error || !data || !data.statistics) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info" sx={{ fontSize: '1.1rem', py: 2 }}>{error || "No hay datos disponibles para mostrar."}</Alert>
      </Box>
    );
  }

  const stats = data.statistics;
  const reviews = data.reviews || [];

  const chartData = [
    { stars: '5 Estrellas', count: stats.ratingDistribution["5"] || 0 },
    { stars: '4 Estrellas', count: stats.ratingDistribution["4"] || 0 },
    { stars: '3 Estrellas', count: stats.ratingDistribution["3"] || 0 },
    { stars: '2 Estrellas', count: stats.ratingDistribution["2"] || 0 },
    { stars: '1 Estrella', count: stats.ratingDistribution["1"] || 0 },
  ];

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#f8fafc', minHeight: '100vh', borderRadius: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GoogleIcon sx={{ fontSize: 40, color: '#4285F4' }} />
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1e293b' }}>
            Estadísticas de Google Reviews
          </Typography>
        </Box>
        <Box sx={{ width: 300 }}>
          <Formik 
            initialValues={{ location: selectedLocation || null }} 
            onSubmit={() => {}}
            enableReinitialize
          >
            <Form>
              <AutocompleteField  
                  options={google_bussinesses_list_for_select || []}
                  name="location"
                  label="Negocio / Ubicación"
                  onChange={(event: any, newValue: any) => setSelectedLocation(newValue)}
              />
            </Form>
          </Formik>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Tarjeta de Calificación Promedio */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
              <Typography variant="h6" color="text.secondary" fontWeight="medium" gutterBottom>
                Calificación Promedio
              </Typography>
              <Typography variant="h1" fontWeight="bold" sx={{ color: '#0f172a', my: 2 }}>
                {stats.averageRating.toFixed(1)}
              </Typography>
              <Rating 
                value={stats.averageRating} 
                precision={0.1} 
                readOnly 
                size="large" 
                emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
              />
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 2 }}>
                Basado en {stats.totalReviews} reseñas
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Tarjeta de Distribución */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ height: '100%', borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" color="text.secondary" fontWeight="medium" gutterBottom>
                Distribución de Calificaciones
              </Typography>
              <Box sx={{ height: 300, width: '100%', mt: 2 }}>
                <BarChart
                  dataset={chartData}
                  yAxis={[{ scaleType: 'band', dataKey: 'stars' }]}
                  series={[{ dataKey: 'count', color: '#f59e0b' }]}
                  layout="horizontal"
                  margin={{ left: 100, right: 20, top: 20, bottom: 20 }}
                  borderRadius={4}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sección de Reseñas */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, mt: 4, color: '#1e293b' }}>
            Últimas Reseñas
          </Typography>
          <Grid container spacing={3}>
            {reviews.map((review) => {
              const numericRating = mapStarRatingToNumber(review.starRating);
              const isPositive = numericRating >= 4;
              return (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={review.reviewId}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
                      borderColor: theme.palette.primary.light
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box display="flex" gap={2} alignItems="center">
                        <Avatar 
                          src={review.reviewer.profilePhotoUrl} 
                          alt={review.reviewer.displayName}
                          sx={{ width: 48, height: 48, border: '2px solid white', boxShadow: 1 }}
                        >
                          {review.reviewer.displayName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                            {review.reviewer.displayName || 'Usuario Anónimo'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(review.createTime)}
                          </Typography>
                        </Box>
                      </Box>
                      <GoogleIcon sx={{ color: '#EA4335', fontSize: 20 }} />
                    </Box>
                    
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Rating 
                        value={numericRating} 
                        readOnly 
                        size="small" 
                        sx={{
                          color: '#FBBC04'
                        }}
                      />
                      <Chip 
                        label={`${numericRating}.0`} 
                        size="small" 
                        sx={{ 
                          height: 20, 
                          fontSize: '0.75rem', 
                          fontWeight: 'bold',
                          bgcolor: isPositive ? alpha('#34A853', 0.1) : alpha('#EA4335', 0.1),
                          color: isPositive ? '#34A853' : '#EA4335'
                        }} 
                      />
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        lineHeight: 1.6,
                        mb: 2,
                        fontStyle: !review.comment ? 'italic' : 'normal'
                      }}
                    >
                      {review.comment ? `"${review.comment}"` : "El usuario no dejó ningún comentario, solo la calificación."}
                    </Typography>

                    {review.reviewReply && (
                      <Box 
                        sx={{ 
                          p: 2, 
                          bgcolor: alpha(theme.palette.primary.main, 0.04), 
                          borderRadius: 2,
                          borderLeft: `3px solid ${theme.palette.primary.main}`
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <ReplyIcon fontSize="small" color="primary" />
                          <Typography variant="caption" fontWeight="bold" color="primary">
                            Respuesta del propietario
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8rem' }}>
                          {review.reviewReply.comment}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )})}
            {reviews.length === 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ borderRadius: 3 }}>No se encontraron reseñas para mostrar.</Alert>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};